import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import StepperComponent from "components/blocks/stepper/Stepper";
import { useEffect, useState } from "react";
import KeyType from "../common/KeyType";
import { ButtonWrapper, ValidateWrapper } from "styles/ModalCommonStyled";
import BigButton from "components/atoms/buttons/BigButton";
import RequestKeyInfo from "./RequestKeyInfo";
import {
  EuRequestType,
  EuRequestResponse,
  SUBSCRIPTION_TYPE,
  EuRequestFormType,
} from "api/interfaces/subscription.interface";
import useStatusMutation from "hook/useStatusMutation";
import { getRequestKeyType, postRequestKey } from "api/subscriptionAPI";
import { CustomError, LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useQuery, useQueryClient } from "react-query";
import {
  ProfileAccountInfo,
} from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useAuth } from "components/Auth";
import useApiError from "hook/useApiError";
import { ValidateWord } from "components/atoms/text/Text";

type Props = {
  onModal: () => void;
};
const steps = [
  {
    label: "Select Key Type",
  },
  {
    label: "Key Info",
  },
];

export default function RequestKeyModal(props: Props): JSX.Element {
  const { handleErrorNotify, intlString, notifySuccess, notifyError, lang } =
    useApiError();
  const { user } = useAuth();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const initialData: EuRequestFormType = {
    keyType: undefined,
    requester: user.email,
    installer: "",
    channels: 1,
    capacity: 1,
    duration: undefined,
    message: "",
  };
  const [page, setPage] = useState<number>(0);

  const [requestKeyData, setRequestKeyData] = useState<EuRequestFormType>({
    ...initialData,
  });

  const [validateMsg, setValidateMsg] = useState<Record<string, string>>({
    keyType: "",
    number: "",
    duration: "",
  });
  const [isDisabledNext, setIsDisabledNext] = useState<boolean>(true);

  const { data } = useQuery(
    ["getRequestKeyType", selectedAccount.accountId],
    () =>
      getRequestKeyType({
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 0,
      enabled: Boolean(requestKeyData.keyType),
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {},
      onError: (err: CustomError) => {
        handleErrorNotify(err, lang.label.subscription.get.keyType.fail);
      },
    }
  );

  const queryClient = useQueryClient();

  const mutationRequestKey = useStatusMutation(postRequestKey, {
    onSuccess: (res: EuRequestResponse) => {
      notifySuccess(lang.label.subscription.request.success);
      props.onModal();
      queryClient.invalidateQueries("getSubscriptonRequestList");
      queryClient.invalidateQueries("getSubscriptionKeyList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.request.fail);
    },
  });

  const onRequestKey = () => {
    setValidateMsg({
      keyType: "",
      number: "",
      duration: "",
    });
    if (!mutationRequestKey.idle) {
      return;
    }
    const value =
      requestKeyData.keyType === SUBSCRIPTION_TYPE.STANDARD
        ? requestKeyData.channels
        : requestKeyData.capacity;
    if (!requestKeyData.duration) {
      setValidateMsg((msg) => {
        return {
          ...msg,
          duration: intlString(
            lang.label.subscription.duration.essential.validate
          ),
        };
      });
      return;
    }
    if (value === 0) {
      setValidateMsg((msg) => {
        return {
          ...msg,
          number: intlString(lang.label.subscription.key.essential.validate),
        };
      });
      return;
    }

    mutationRequestKey.mutate({
      accountId: selectedAccount.accountId,
      payload: requestKeyData,
    });
  };

  const onChangePage = (name: string, index?: number) => {
    if (name === "next") {
      if (page === 0 && !requestKeyData.keyType) {
        setValidateMsg((msg) => {
          return {
            ...msg,
            keyType: intlString(
              lang.label.subscription.keyType.essential.validate
            ),
          };
        });
        return;
      }
      setPage(page + 1);
    }
    if (name === "previous") {
      setPage(page - 1);
    }
    if (name === "save") {
      props.onModal();
    }
    if (name === "step" && index !== undefined) {
      if (page === 0 && index === 1 && !requestKeyData.keyType) {
        setValidateMsg((msg) => {
          return {
            ...msg,
            keyType: intlString(
              lang.label.subscription.keyType.essential.validate
            ),
          };
        });
        return;
      }
      setPage(index);
    }
  };

  const onChangeKeyData = (name: string, value: SUBSCRIPTION_TYPE | number) => {
    if (name === "duration" && value) {
      setValidateMsg((msg) => {
        return {
          ...msg,
          duration: "",
        };
      });
    }

    if (
      requestKeyData.keyType === SUBSCRIPTION_TYPE.STANDARD
        ? name === "channels" && value
        : name === "capacity" && value
    ) {
      if (name === "capacity") {
        const strValue = String(value).replace(/\D/g, "");
        if (strValue.length > 7) {
          return;
        }
      }
      setValidateMsg((msg) => {
        return {
          ...msg,
          number: "",
        };
      });
    }

    setRequestKeyData((prev: any) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    if (data?.result && data.error === 0) {
      setRequestKeyData((prev) => {
        return {
          ...prev,
          installer: data.result.owner.email || "",
        };
      });
    } else {
      setRequestKeyData((prev) => {
        return { ...initialData, keyType: prev.keyType };
      });
    }
  }, [data]);

  useEffect(() => {
    const value =
      requestKeyData.keyType === SUBSCRIPTION_TYPE.STANDARD
        ? requestKeyData.channels
        : requestKeyData.capacity;
    if (value !== 0 && requestKeyData.duration) {
      setValidateMsg({
        keyType: "",
        number: "",
        duration: "",
      });
      setIsDisabledNext(false);
    }
  }, [requestKeyData]);

  return (
    <BigPopupTemplate title="Request Subscription Key" onModal={props.onModal}>
      <StepperComponent
        activeStep={page}
        steps={steps}
        onClick={onChangePage}
        data-testid="subscription-modal-request-key-stepper-component-change-page"
      />

      {page === 0 && (
        <>
          <KeyType
            keyType={requestKeyData.keyType}
            onChangeType={(value: SUBSCRIPTION_TYPE) =>
              onChangeKeyData("keyType", value)
            }
          />

          {validateMsg.keyType && (
            <ValidateWrapper>
              <ValidateWord data-testid="subscription-modal-request-key-validate-key-type">
                {validateMsg.keyType}
              </ValidateWord>
            </ValidateWrapper>
          )}
        </>
      )}

      {page === 1 && requestKeyData.keyType && (
        <RequestKeyInfo
          keyInfo={requestKeyData as EuRequestType}
          onChangeKeyData={onChangeKeyData}
          validateMsg={validateMsg}
        />
      )}

      <ButtonWrapper>
        {page === 0 && (
          <BigButton
            label={"Next"}
            buttonType={!requestKeyData.keyType ? "cancel" : "primary"}
            onClickBtn={() => onChangePage("next")}
            disabled={!requestKeyData.keyType}
            data-testid="subscription-modal-request-key-button-change-page"
          />
        )}

        {page !== 0 && (
          <BigButton
            label={"Previous"}
            buttonType={"cancel"}
            onClickBtn={() => onChangePage("previous")}
            data-testid="subscription-modal-request-key-button-change-page-2"
          />
        )}

        {page === 1 && (
          <BigButton
            label="Request Key"
            onClickBtn={onRequestKey}
            buttonType={isDisabledNext ? "cancel" : "primary"}
            data-testid="subscription-modal-request-key-button-request-key"
          />
        )}
      </ButtonWrapper>
    </BigPopupTemplate>
  );
}
